import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Helmet } from "react-helmet";
import { ChevronDown, ChevronUp, ShoppingCart } from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getProfileFromResponse,
  getProfileUserId,
  getUserDetails,
  updateUserProfile,
} from "../../services/Users";
import { clearCarts, getProducts, syncCartItems } from "../../services/productService";
import AvailableOn from "../AvailableOn/AvailableOn";
import { clearAllLocalStorage, saveAuthenticatedProfile } from "../../utils/authStorage";
import "./Profile.css";

const EMPTY_PROFILE = {
  fullName: "",
  mobile: "",
  email: "",
  gstNo: "",
  address: "",
  city: "",
  pincode: "",
  state: "",
};

const getStoredValue = (key) => {
  if (typeof window === "undefined") {
    return "";
  }

  return window.localStorage.getItem(key) || "";
};

const getInitialProfile = () => ({
  ...EMPTY_PROFILE,
  fullName: getStoredValue("auth_name") || getStoredValue("profile_full_name"),
  mobile: getStoredValue("auth_mobile") || getStoredValue("profile_mobile"),
  email: getStoredValue("profile_email"),
  gstNo: getStoredValue("profile_gst_no"),
  address: getStoredValue("profile_address"),
  city: getStoredValue("profile_city"),
  pincode: getStoredValue("profile_pincode"),
  state: getStoredValue("profile_state"),
});

const formatOrderDate = (date) => {
  if (!date) {
    return "";
  }

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(parsedDate);
};

const getOrderId = (order) =>
  order?.order_id || order?.order_no || order?.id || "KN00123";

const getOrderTotal = (order) =>
  order?.total_amount || order?.total || order?.grand_total || "0";

const formatCurrency = (value) => {
  const amount = Number.parseFloat(value || 0);

  if (!Number.isFinite(amount)) {
    return "0";
  }

  return amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2);
};

const getOrderKey = (order, index) => String(order?.id || order?.order_id || index);

const getLookupProduct = (item, productMap) =>
  productMap[String(item?.product_id)] || item?.product || item?.product_details || {};

const getProductName = (item, productMap) => {
  const product = getLookupProduct(item, productMap);

  return (
    item?.product_name ||
    item?.name ||
    item?.product_details?.product_name ||
    product?.product_name ||
    product?.name ||
    `Product #${item?.product_id || ""}`.trim()
  );
};

const getProductWeight = (item, productMap) => {
  const product = getLookupProduct(item, productMap);

  return (
    item?.weight ||
    item?.variant_weight ||
    item?.product_details?.weight ||
    product?.weight ||
    product?.variant_weight ||
    ""
  );
};

const buildProductMap = (products = []) =>
  products.reduce((map, product) => {
    const productId = product?.product_id || product?.id;
    const productName = product?.product_name || product?.name;

    if (productId) {
      map[String(productId)] = {
        ...product,
        product_name: productName,
        name: productName,
      };
    }

    const variants = [
      ...(Array.isArray(product?.variant_data) ? product.variant_data : []),
      ...(Array.isArray(product?.variants) ? product.variants : []),
    ];

    variants.forEach((variant) => {
      const variantId = variant?.product_id || variant?.id;

      if (!variantId) {
        return;
      }

      map[String(variantId)] = {
        ...product,
        ...variant,
        product_id: variantId,
        id: variantId,
        parent_product_id: productId,
        product_name: productName,
        name: productName,
        thumbnail_image: product?.thumbnail_image || variant?.thumbnail_image,
        product_price: variant?.product_price || product?.product_price,
        product_sell_price: variant?.product_sell_price || product?.product_sell_price,
        weight: variant?.weight || product?.weight,
        variant_weight: variant?.weight || product?.weight,
      };
    });

    return map;
  }, {});

const PROFILE_TABS = [
  { id: "dashboard", label: "Dashboard" },
  { id: "profile", label: "Profile" },
  { id: "track-order", label: "Track Order" },
];

function ProfileInput({
  id,
  name,
  type = "text",
  label,
  required,
  value,
  onChange,
  maxLength,
  inputMode,
}) {
  return (
    <div className={`profile-input-wrap${value ? " has-value" : ""}`}>
      <input
        id={id}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        maxLength={maxLength}
        inputMode={inputMode}
        autoComplete="off"
      />
      <span className="profile-placeholder">
        {label} {required && <sup>*</sup>}
      </span>
    </div>
  );
}

function OrderHistoryContent({ orders, productMap, onRepeatOrder, repeatingOrderKey }) {
  const [openOrderKey, setOpenOrderKey] = useState("");

  useEffect(() => {
    setOpenOrderKey(orders.length > 0 ? getOrderKey(orders[0], 0) : "");
  }, [orders]);

  return (
    <div className="profile-tab-content">
      <h2>Order History</h2>
      {orders.length > 0 ? (
        <div className="profile-order-list">
          {orders.map((order, index) => {
            const orderKey = getOrderKey(order, index);
            const isOpen = openOrderKey === orderKey;
            const items = Array.isArray(order?.items) ? order.items : [];

            return (
              <div className="profile-order-card" key={orderKey}>
                <div
                  className={`profile-order-row${isOpen ? " active" : ""}`}
                  onClick={() => setOpenOrderKey(isOpen ? "" : orderKey)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setOpenOrderKey(isOpen ? "" : orderKey);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isOpen}
                >
                  <span className="profile-order-summary">
                    <strong>Order Id: #{getOrderId(order)}</strong>
                    <span>|</span>
                    <strong>Date: {formatOrderDate(order?.created_at)}</strong>
                    <span>|</span>
                    <strong>Total: Rs.{formatCurrency(getOrderTotal(order))}/-</strong>
                    <span>|</span>
                    <button
                      type="button"
                      className="profile-repeat-btn"
                      disabled={repeatingOrderKey === orderKey}
                      onClick={(event) => {
                        event.stopPropagation();
                        onRepeatOrder(order, orderKey);
                      }}
                    >
                      <ShoppingCart size={17} />
                      {repeatingOrderKey === orderKey ? "Adding..." : "Repeat Order"}
                    </button>
                  </span>
                  <span className="profile-order-toggle" aria-label="View order">
                    {isOpen ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                  </span>
                </div>

                <div
                  className={`profile-order-detail-wrap${isOpen ? " open" : ""}`}
                  aria-hidden={!isOpen}
                >
                  <div className="profile-order-detail">
                    <div className="profile-order-detail-inner">
                      {items.length > 0 ? (
                        items.map((item, itemIndex) => (
                          <div
                            className="profile-order-item"
                            key={item?.id || `${orderKey}-${itemIndex}`}
                          >
                            <div>
                              <strong>{getProductName(item, productMap)}</strong>
                              {getProductWeight(item, productMap) && (
                                <span>{getProductWeight(item, productMap)}</span>
                              )}
                            </div>
                            <strong>{item?.quantity || 1} pkt</strong>
                            <strong>Rs.{formatCurrency(item?.price)}/-</strong>
                          </div>
                        ))
                      ) : (
                        <p className="profile-empty-text">No item details found.</p>
                      )}

                      <div className="profile-order-meta">
                        <span>Status: {order?.status || "Pending"}</span>
                        <span>Payment: {order?.payment_status || "Pending"}</span>
                        <span>Mode: {order?.payment_mode || "-"}</span>
                        <span>Delivery: Rs.{formatCurrency(order?.delivery_charge)}/-</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="profile-empty-text">No orders found.</p>
      )}
    </div>
  );
}

const Profile = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState(getInitialProfile);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [orders, setOrders] = useState([]);
  const [productMap, setProductMap] = useState({});
  const [isOrdersLoading, setIsOrdersLoading] = useState(false);
  const [repeatingOrderKey, setRepeatingOrderKey] = useState("");
  const token = getStoredValue("token");

  const { data: user } = useQuery({
    queryFn: getUserDetails,
    queryKey: ["user_profile_info"],
    enabled: Boolean(token),
    retry: false,
  });

  const apiProfile = getProfileFromResponse(user);
  const userId = getProfileUserId(apiProfile);
  const totalPurchase =
    apiProfile?.total_purchase || apiProfile?.totalPurchase || 0;

  const mutateUserProfile = useMutation({
    mutationFn: updateUserProfile,
    mutationKey: ["upload_user_profile"],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user_profile_info"] });
      toast.success("Profile saved successfully");
    },
    onError: (error) => {
      console.error(error);
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.errors ||
        "Profile saved locally, but API update failed";
      toast.error(message);
    },
  });

  useEffect(() => {
    if (!user) {
      return;
    }

    const profile = getProfileFromResponse(user);

    const nextUserId = getProfileUserId(profile);
    if (nextUserId) {
      localStorage.setItem("user_id", String(nextUserId));
    }

    saveAuthenticatedProfile(profile, { userId: nextUserId });

    setForm({
      ...EMPTY_PROFILE,
      fullName: profile?.first_name || profile?.name || "",
      mobile: profile?.phone_no || profile?.mobile || "",
      email: profile?.email || "",
      gstNo: profile?.gst_no || profile?.gst_number || "",
      address: profile?.address || "",
      city: profile?.city || "",
      pincode: profile?.pincode || "",
      state: profile?.state || "",
    });
  }, [user]);

  useEffect(() => {
    if (!token && !userId) {
      return;
    }

    const fetchOrders = async () => {
      setIsOrdersLoading(true);

      try {
        const [ordersResponse, productsResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/order/list-all`, {
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
              token,
            },
          }),
          getProducts({ queryKey: ["all"] }),
        ]);
        const orderList = Array.isArray(ordersResponse?.data?.data)
          ? ordersResponse.data.data
          : [];
        const currentUserOrders = userId
          ? orderList.filter((order) => String(order?.user_id) === String(userId))
          : orderList;
        const nextProductMap = buildProductMap(productsResponse?.data || []);

        setOrders(currentUserOrders);
        setProductMap(nextProductMap);
      } catch (error) {
        console.error("Error fetching order details", error);
      } finally {
        setIsOrdersLoading(false);
      }
    };

    fetchOrders();
  }, [token, userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "mobile") {
      setForm((current) => ({
        ...current,
        mobile: value.replace(/\D/g, "").slice(0, 10),
      }));
      return;
    }

    if (name === "pincode") {
      setForm((current) => ({
        ...current,
        pincode: value.replace(/\D/g, "").slice(0, 6),
      }));
      return;
    }

    setForm((current) => ({ ...current, [name]: value }));
  };

  const persistProfileLocally = () => {
    localStorage.setItem("auth_name", form.fullName);
    localStorage.setItem("auth_mobile", form.mobile);
    localStorage.setItem("profile_full_name", form.fullName);
    localStorage.setItem("profile_mobile", form.mobile);
    localStorage.setItem("profile_email", form.email);
    localStorage.setItem("profile_gst_no", form.gstNo);
    localStorage.setItem("profile_address", form.address);
    localStorage.setItem("profile_city", form.city);
    localStorage.setItem("profile_pincode", form.pincode);
    localStorage.setItem("profile_state", form.state);
  };

  const buildProfilePayload = () => {
    const payload = new FormData();

    payload.append("gstno", form.gstNo.trim());
    payload.append("email", form.email.trim());
    payload.append("address", form.address.trim());
    payload.append("city", form.city.trim());
    payload.append("pincode", form.pincode.trim());
    payload.append("state", form.state.trim());

    return payload;
  };


  const keyCartItems = (items = []) =>
    items.reduce((acc, item) => {
      if (item?.product_id) {
        acc[String(item.product_id)] = item;
      }

      return acc;
    }, {});

  const buildRepeatCartItems = (order) => {
    const orderItems = Array.isArray(order?.items) ? order.items : [];

    return orderItems
      .map((item) => {
        const product = productMap[String(item?.product_id)] || item?.product || {};
        const productId = item?.product_id || product?.product_id || product?.id;

        if (!productId) {
          return null;
        }

        return {
          id: productId,
          product_id: productId,
          product_name: getProductName(item, productMap),
          thumbnail_image:
            product?.thumbnail_image ||
            item?.thumbnail_image ||
            item?.product_image ||
            product?.image ||
            "",
          quantity: Math.max(1, Number(item?.quantity || 1)),
          price: Number(item?.price || product?.product_price || product?.price || 0),
          originalPrice: Number(product?.product_sell_price || product?.originalPrice || 0),
          discount: Number(product?.discount || product?.product_discount || 0),
          weight: getProductWeight(item, productMap),
        };
      })
      .filter(Boolean);
  };

  const handleRepeatOrder = async (order, orderKey) => {
    const repeatItems = buildRepeatCartItems(order);

    if (repeatItems.length === 0) {
      toast.info("No products found for this order");
      return;
    }

    if (!userId) {
      toast.info("Please login again to repeat this order");
      return;
    }

    try {
      setRepeatingOrderKey(orderKey);
      localStorage.setItem("carts", JSON.stringify(keyCartItems(repeatItems)));
      queryClient.setQueriesData({ queryKey: ["get-cart"] }, (oldData) => ({
        ...(oldData || {}),
        data: {
          ...(oldData?.data || {}),
          data: keyCartItems(repeatItems),
        },
      }));
      await syncCartItems({ user_id: userId, items: repeatItems });
      await queryClient.invalidateQueries({ queryKey: ["get-cart"] });
      navigate("/checkout");
    } catch (error) {
      console.error("Failed repeating order", error);
      toast.error("Failed adding order items to cart");
    } finally {
      setRepeatingOrderKey("");
    }
  };
  const handleLogout = async () => {
    const logoutUserId = userId || localStorage.getItem("user_id");
    const logoutToken = localStorage.getItem("token");

    if (logoutToken && logoutUserId) {
      try {
        await clearCarts({ user_id: logoutUserId });
      } catch (error) {
        console.error("Failed clearing cart during logout", error);
      }
    }

    clearAllLocalStorage();
    window.dispatchEvent(new CustomEvent("auth-state-changed"));
    queryClient.removeQueries({ queryKey: ["user_profile_info"] });
    queryClient.removeQueries({ queryKey: ["get-cart"] });
    toast.success("Logged out successfully");
    navigate("/");
  };
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!form.fullName.trim()) {
      toast.info("Full Name is required");
      return;
    }

    if (!form.mobile || form.mobile.length !== 10) {
      toast.info("Please enter a valid 10 digit mobile number");
      return;
    }

    if (!form.email.trim()) {
      toast.info("Email is required");
      return;
    }

    if (!form.address.trim()) {
      toast.info("Address is required");
      return;
    }

    if (!form.city.trim()) {
      toast.info("City is required");
      return;
    }

    if (!form.pincode || form.pincode.length !== 6) {
      toast.info("Please enter a valid 6 digit pincode");
      return;
    }

    if (!form.state.trim()) {
      toast.info("State is required");
      return;
    }

    persistProfileLocally();

    if (!token) {
      toast.success("Profile saved locally");
      return;
    }

    mutateUserProfile.mutate(buildProfilePayload());
  };

  return (
    <>
      <Helmet>
        <title>Kuku Namkeen | Profile</title>
        <meta
          name="description"
          content="Manage your Kuku Namkeen profile and shipping details."
        />
        <link rel="canonical" href="https://kukunamkeen.in/profile" />
      </Helmet>

      <main className="profile-page">
        <section className="section-header-title">
          <div className="max-w-6xl mx-auto px-4">
            <h1>PROFILE</h1>
          </div>
        </section>
        <img
          src="/Image/border-bottom-title.png"
          className="w-full header-title-image"
          alt=""
        />

        <section className="profile-content">
          <div className="profile-panel">
            <div className="profile-panel-top">
              <p>
                <strong>Hello,</strong> {form.fullName || "Person Name"}
              </p>
              <p>Total Purchase : {totalPurchase}</p>
            </div>

            <div className="profile-panel-body">
              <aside className="profile-side-nav" aria-label="Profile navigation">
                {PROFILE_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={`profile-side-link${
                      activeTab === tab.id ? " active" : ""
                    }`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </aside>

              {activeTab === "dashboard" &&
                (isOrdersLoading ? (
                  <div className="profile-tab-content">
                    <h2>Order History</h2>
                    <p className="profile-empty-text">Loading orders...</p>
                  </div>
                ) : (
                  <OrderHistoryContent
                    orders={orders}
                    productMap={productMap}
                    onRepeatOrder={handleRepeatOrder}
                    repeatingOrderKey={repeatingOrderKey}
                  />
                ))}

              {activeTab === "profile" && (
              <form className="profile-form" onSubmit={handleSubmit}>
                <h2>Save Your Details for Easy Shipping</h2>

                <div className="profile-form-grid">
                  <ProfileInput
                    id="profile-full-name"
                    name="fullName"
                    label="Full Name"
                    required
                    value={form.fullName}
                    onChange={handleChange}
                  />
                  <ProfileInput
                    id="profile-mobile"
                    name="mobile"
                    type="tel"
                    label="Your Mobile"
                    required
                    value={form.mobile}
                    onChange={handleChange}
                    maxLength={10}
                    inputMode="numeric"
                  />
                  <ProfileInput
                    id="profile-email"
                    name="email"
                    type="email"
                    label="mail@yourmail.com"
                    required
                    value={form.email}
                    onChange={handleChange}
                  />
                  <ProfileInput
                    id="profile-gst"
                    name="gstNo"
                    label="GST No. (Optional)"
                    value={form.gstNo}
                    onChange={handleChange}
                  />
                  <ProfileInput
                    id="profile-address"
                    name="address"
                    label="Address"
                    required
                    value={form.address}
                    onChange={handleChange}
                  />
                  <ProfileInput
                    id="profile-city"
                    name="city"
                    label="City"
                    required
                    value={form.city}
                    onChange={handleChange}
                  />
                  <ProfileInput
                    id="profile-pincode"
                    name="pincode"
                    type="tel"
                    label="Pincode"
                    required
                    value={form.pincode}
                    onChange={handleChange}
                    maxLength={6}
                    inputMode="numeric"
                  />
                  <ProfileInput
                    id="profile-state"
                    name="state"
                    label="State"
                    required
                    value={form.state}
                    onChange={handleChange}
                  />
                </div>

                <div className="profile-upload-row">
                  <div className="profile-avatar-actions">
                    {/* <div className="profile-avatar-wrap">
                      <img
                        src={previewUrl || getProfileImage(apiProfile)}
                        alt="Profile avatar"
                      />
                    </div> */}
                    <button
                      className="profile-save-btn"
                      type="submit"
                      disabled={mutateUserProfile.isLoading}
                    >
                      {mutateUserProfile.isLoading ? "Saving..." : "Save"}
                    </button>
                  </div>
                  <div className="profile-logout-actions">
                    <button
                      className="profile-logout-btn"
                      type="button"
                      onClick={handleLogout}
                    >
                      Logout
                    </button>
                  </div>
                  {/* <div className="profile-file-wrap">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleProfileImageChange}
                    />
                    <p>Maximum image size : 5mb</p>
                  </div> */}
                </div>
              </form>
              )}

              {activeTab === "track-order" && (
                <div className="profile-tab-content profile-track-empty"></div>
              )}
            </div>
          </div>
        </section>

        <AvailableOn />
      </main>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </>
  );
};

export default Profile;
